import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resizeImage } from 'utils/resizeImage';
import { getImageUrl } from 'utils/image.util';

import { useAlertStore, useUserStore } from 'store';
import { updateProfile } from 'apis';

import { ReactComponent as CameraIcon } from 'assets/images/camera.icon.svg'
import { ERROR, ResponseDTO } from 'types';
import { AxiosError } from 'axios';


interface ProfileComponentProps {
    isEditProfile?: boolean;
    onSave?: boolean;
    onSaveFailure?: () => void;
    onFollowClick?: () => void;
    showFollowComponent?: boolean;
}

interface EditAreaProps {
    onSave?: boolean;
    selectedProfileImage?: File | null;
    selectedBannerImage?: File | null;
    onSaveFailure?: () => void;
}

const EditArea = ({ onSave, selectedProfileImage, selectedBannerImage, onSaveFailure }: EditAreaProps) => {
    const showAlert = useAlertStore((state) => state.showAlert);
    const { user, setUser } = useUserStore();
    const navigate = useNavigate();

    const [profileId, setProfileId] = useState<string>(user?.profileId || '');
    const [nickname, setNickname] = useState<string>(user?.name || '');
    const [bio, setBio] = useState<string>(user?.bio || '');
    const [phone, setPhone] = useState<string>(user?.phone || '');

    const [isError, setIsError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const saveEditedProfile = async () => {
        try {
            const response = await updateProfile(
                profileId, nickname,
                undefined,
                undefined, phone || undefined,
                selectedProfileImage, selectedBannerImage,
                bio,
            );
            if (response.data.isSuccess === 'T') {
                setUser(response.data.data);
                navigate('/mypage');
                window.location.reload();
            }
        } catch (error) {
            const axiosError = error as AxiosError<ResponseDTO<any>>;
            if (axiosError.response?.data?.message === ERROR.EXIST_PROFILE_ID) {
                showAlert('이미 존재하는 프로필 아이디입니다.', 'error');
                onSaveFailure && onSaveFailure();
                setIsError(true);
                setErrorMessage('사용 중인 아이디입니다. 다른 아이디를 입력해주세요.');
            }
        }
    }

    useEffect(() => {
        if (onSave) { saveEditedProfile(); }
    }, [onSave])

    useEffect(() => {
        if (isError) { setIsError(false); }
    }, [profileId])

    return (
        <div className='flex flex-col gap-6 p-6 pt-0 text-achromatic-400 font-medium text-sm'>
            <div className='flex flex-col gap-2'>
                <span>아이디</span>
                <input
                    className='px-3 min-h-12 border border-achromatic-200 rounded-xl text-achromatic-900 font-semibold focus:outline-none focus:border-achromatic-400'
                    value={profileId} onChange={(e) => setProfileId(e.target.value)} />
                {isError && <div className='text-red-500 font-medium'>{errorMessage}</div>}
                <span className='text-achromatic-300'>@{profileId}</span>
            </div>
            <div className='flex flex-col gap-2'>
                <span>닉네임</span>
                <input
                    className='px-3 min-h-12 border border-achromatic-200 rounded-xl text-achromatic-900 font-semibold focus:outline-none focus:border-achromatic-400'
                    value={nickname} onChange={(e) => setNickname(e.target.value)} />
            </div>

            <div className='flex flex-col gap-2'>
                <span>소개</span>
                <textarea
                    className='px-3 py-3 min-h-[80px] border border-achromatic-200 rounded-xl placeholder-achromatic-300 text-achromatic-900 font-semibold focus:outline-none focus:border-achromatic-400 resize-none'
                    value={bio} placeholder="간단한 자기소개를 적어주세요."
                    maxLength={200}
                    onChange={(e) => setBio(e.target.value)} />
                <span className='text-[11px] text-achromatic-300 text-right'>{bio.length}/200</span>
            </div>

            <div className='flex flex-col gap-2'>
                <span>전화번호</span>
                <input
                    className='px-3 min-h-12 border border-achromatic-200 rounded-xl text-achromatic-900 font-semibold focus:outline-none focus:border-achromatic-400'
                    value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder='01012345678'
                    maxLength={11} />
                <span className='text-[11px] text-achromatic-300'>📱 전화번호를 등록하면 예약·결제 알림톡을 받을 수 있어요.</span>
            </div>
        </div>
    )
}

const ProfileComponent = ({ isEditProfile, onSave, onSaveFailure, onFollowClick, showFollowComponent }: ProfileComponentProps) => {
    const navigate = useNavigate();
    const { user } = useUserStore();

    const [selectedProfileImage, setSelectedProfileImage] = useState<File | null>(null);
    const [selectedBannerImage, setSelectedBannerImage] = useState<File | null>(null);
    const [followerCount, setFollowerCount] = useState(0);
    const [followingCount, setFollowingCount] = useState(0);

    const profileImageInputRef = useRef<HTMLInputElement>(null);
    const bannerImageInputRef = useRef<HTMLInputElement>(null);

    const handleEditProfileClick = () => navigate('/mypage/edit-profile');
    const handleEditProfileImageClick = () => profileImageInputRef.current?.click();
    const handleEditBannerImageClick = () => bannerImageInputRef.current?.click();

    const handleProfileImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            try {
                const resized = await resizeImage(file, 'profile');
                setSelectedProfileImage(resized);
            } catch {
                setSelectedProfileImage(file);
            }
        }
    }

    const handleBannerImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            try {
                const resized = await resizeImage(file, 'banner');
                setSelectedBannerImage(resized);
            } catch {
                setSelectedBannerImage(file);
            }
        }
    }

    // Fetch follower/following counts
    useEffect(() => {
        if (!user?.id) return;
        import('apis').then(({ getFollowerCount, getFollowingList }) => {
            getFollowerCount(user.id).then(res => setFollowerCount(res.data.data)).catch(() => {});
            getFollowingList(1, 1).then(res => setFollowingCount(res.data.data.total)).catch(() => {});
        });
    }, [user?.id]);

    return (
        <div className="flex flex-col">
            {/* Banner */}
            <div className="relative">
                {user?.bannerImage || selectedBannerImage ? (
                    <img
                        src={selectedBannerImage ? URL.createObjectURL(selectedBannerImage) : getImageUrl(user?.bannerImage)}
                        alt="banner"
                        className="w-full h-[200px] object-cover rounded-t-2xl mobile:rounded-t-none"
                    />
                ) : (
                    <div className="w-full h-[160px] bg-gradient-to-br from-primary-50 to-violet-50 rounded-t-2xl mobile:rounded-t-none" />
                )}
                {isEditProfile && (
                    <div className='absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm border border-achromatic-200 rounded-full p-2 cursor-pointer hover:bg-white transition-colors shadow-sm'
                        onClick={handleEditBannerImageClick}>
                        <CameraIcon className='w-4 h-4' />
                        <input className='hidden' ref={bannerImageInputRef} onChange={handleBannerImageUpload} type="file" accept="image/*" />
                    </div>
                )}
            </div>

            {/* Profile info */}
            <div className="flex justify-between p-6 mobile:p-4">
                <div className="flex gap-3">
                    <div className='relative flex'>
                        {user?.profileImage || selectedProfileImage ? (
                            <img
                                src={selectedProfileImage ? URL.createObjectURL(selectedProfileImage) : getImageUrl(user?.profileImage)}
                                alt="profile"
                                className="w-16 h-16 object-cover rounded-full border-2 border-white shadow-sm"
                            />
                        ) : (
                            <div className="w-16 h-16 bg-achromatic-100 rounded-full flex items-center justify-center text-xl text-achromatic-300">👤</div>
                        )}
                        {isEditProfile && (
                            <div className='absolute bottom-0 right-0 bg-white/90 backdrop-blur-sm border border-achromatic-200 rounded-full p-1.5 cursor-pointer hover:bg-white transition-colors shadow-sm'
                                onClick={handleEditProfileImageClick}>
                                <CameraIcon className='w-3 h-3' />
                                <input className='hidden' ref={profileImageInputRef} onChange={handleProfileImageUpload} type="file" accept="image/*" />
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col justify-center">
                        <div className="text-achromatic-900 font-bold text-[15px]">{user?.name}</div>
                        <div className="text-achromatic-400 text-[13px]">@{user?.profileId}</div>
                        {!isEditProfile && (
                            <div className="flex gap-3 mt-1">
                                <span className="text-[12px] text-achromatic-500">
                                    <span className="font-bold text-achromatic-800">{followerCount}</span> 팔로워
                                </span>
                                <span className="text-[12px] text-achromatic-500">
                                    <span className="font-bold text-achromatic-800">{followingCount}</span> 팔로잉
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                {!isEditProfile && (
                    <div className="flex gap-2 items-center text-xs font-semibold">
                        <button
                            className='text-achromatic-600 rounded-xl border border-achromatic-200 px-4 py-2 text-nowrap hover:bg-achromatic-50 transition-colors font-semibold'
                            onClick={handleEditProfileClick}
                        >
                            프로필 수정
                        </button>
                    </div>
                )}
            </div>

            {/* Bio display */}
            {!isEditProfile && user?.bio && (
                <div className="px-6 pb-4">
                    <p className="text-[13px] text-achromatic-600 leading-relaxed">{user.bio}</p>
                </div>
            )}

            {isEditProfile && (
                <EditArea
                    onSave={onSave} onSaveFailure={onSaveFailure}
                    selectedProfileImage={selectedProfileImage}
                    selectedBannerImage={selectedBannerImage} />
            )}
        </div>
    );
}

export default ProfileComponent;